import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

interface GovernanceProps {
  language: 'pt' | 'en';
}

export default function Governance({ language }: GovernanceProps) {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  const content = {
    pt: {
      eyebrow: 'Método e Disciplina',
      title: 'Governança patrimonial aplicada',
      subtitle: 'Consórcio não é sorte. É estrutura, timing e execução disciplinada.',
      principles: [
        {
          title: 'Método',
          description: 'Análise técnica de grupos, taxas, histórico de contemplações e regulamento. Decisão baseada em dados, não em promessas comerciais.'
        },
        {
          title: 'Timing',
          description: 'Entrada estratégica no grupo certo, no momento certo. Avaliação de ciclo de contemplações, saldo acumulado e dinâmica de lances.'
        },
        {
          title: 'Disciplina',
          description: 'Comprometimento mensal sustentável. Planejamento de fluxo de caixa para evitar inadimplência e garantir permanência até a contemplação.'
        },
        {
          title: 'Execução',
          description: 'Sem improviso. Cada etapa — da adesão ao uso da carta de crédito — segue protocolo técnico e exige acompanhamento operacional.'
        },
        {
          title: 'Governança',
          description: 'Transparência contratual, leitura crítica de cláusulas e alinhamento de expectativas. A FGT Capital promove clareza, evita conflitos futuros.'
        },
        {
          title: 'Economia',
          description: 'Redução efetiva de custo frente ao financiamento convencional. Sem IOF, sem juros compostos — apenas taxa de administração diluída nas parcelas.'
        }
      ]
    },
    en: {
      eyebrow: 'Method and Discipline',
      title: 'Applied wealth governance',
      subtitle: 'Consortium is not luck. It is structure, timing and disciplined execution.',
      principles: [
        {
          title: 'Method',
          description: 'Technical analysis of groups, fees, award history and regulations. Decision based on data, not commercial promises.'
        },
        {
          title: 'Timing',
          description: 'Strategic entry into the right group, at the right time. Evaluation of award cycles, accumulated balance and bidding dynamics.'
        },
        {
          title: 'Discipline',
          description: 'Sustainable monthly commitment. Cash flow planning to avoid default and ensure permanence until award.'
        },
        {
          title: 'Execution',
          description: 'No improvisation. Each stage — from membership to credit letter usage — follows technical protocol and requires operational monitoring.'
        },
        {
          title: 'Governance',
          description: 'Contractual transparency, critical reading of clauses and expectation alignment. FGT Capital promotes clarity, avoids future conflicts.'
        },
        {
          title: 'Economy',
          description: 'Effective cost reduction compared to conventional financing. No IOF, no compound interest — only the administration fee spread across installments.'
        }
      ]
    }
  };

  const t = content[language];

  return (
    <section id="governance" ref={sectionRef} className="relative bg-white border-b border-[#E7E1D9] overflow-hidden">
      {/* Decorative image — right half, grayscale, low opacity */}
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none select-none overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/923515a4e0361119b05038ddca460ce6.jpg"
          alt=""
          className="w-full h-full object-cover object-left-top"
          style={{
            filter: 'grayscale(100%)',
            opacity: isVisible ? 0.12 : 0,
            transform: isVisible ? 'scale(1)' : 'scale(1.06)',
            transition: 'opacity 1.2s ease-out, transform 1.4s ease-out',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="max-w-2xl mb-16">
          <h2 className="text-sm uppercase tracking-widest text-[#2E2E2E] mb-6 font-semibold">
            {t.eyebrow}
          </h2>
          <p className="text-4xl lg:text-5xl font-semibold text-[#0B0B0B] leading-tight mb-4">
            {t.title}
          </p>
          <p className="text-lg text-[#2E2E2E]">
            {t.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.principles.map((principle, index) => (
            <div
              key={index}
              className="border border-[#E7E1D9] bg-[#FBF8F3] p-8 hover:border-[#F97700] transition-colors duration-300"
            >
              <h3 className="text-lg font-semibold text-[#0B0B0B] mb-3 tracking-wide">
                {principle.title}
              </h3>
              <p className="text-base text-[#2E2E2E] leading-relaxed">
                {principle.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}